import PropTypes from 'prop-types';
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { Map, List, is } from 'immutable';
import { connect } from 'react-redux';

import connectOptions, { mergeProps } from '../../utils/connectOptions';
import Popups from '../../components/Popup';
import config from '../../config';
import {
  saveProjectDetails,
  loadProject,
  reset,
  revertProjectChanges,
  updateProjectInfo,
  selectProjectSuggestion,
} from '../../actions/project/main';
import {
  loadUsers,
  insertUserRow,
  switchUserRowToEdit,
  updateUserText,
  selectUserSuggestion,
  deleteUserRow,
} from '../../actions/project/user';
import { loadIndustries, updateIndustryTags } from '../../actions/project/industry';
import {
  updateTemplateName,
  switchTemplateRowToEdit,
  saveTemplateName,
  deleteTemplateRow,
} from '../../actions/project/template';
import { openInternalLink } from '../../utils/url';
import { showError } from '../../utils/MessagePopup';
import { isChanged, getIn, getOriginalIn } from '../../utils/ChangeSpy';
import deletionCheck from '../../utils/deletingPermission';
import downloadFile from '../../utils/downloadFile';
import { usePopupContext } from '../../contexts/PopupContext';

const types = config.values.getIn(['project', 'types']);

/**
 * Container of the popup for editing project details. Popups do not stack, so every popup opened from here
 * (industry tags, template upload, permission alerts) returns to this one when it is closed.
 * Closing this popup discards unsaved changes by reloading the edited data, then calls `onClose` from popup props.
 */
const ProjectDetailsPopupContainer = props => {
  const {
    common,
    deleteTemplateRow,
    deleteUserRow,
    insertUserRow,
    loadIndustries,
    loadProject,
    loadUsers,
    onClose,
    projectData,
    reset,
    revertProjectChanges,
    saveProjectDetails,
    saveTemplateName,
    selectProjectSuggestion,
    selectUserSuggestion,
    switchTemplateRowToEdit,
    switchUserRowToEdit,
    templates,
    updateIndustryTags,
    updateProjectInfo,
    updateTemplateName,
    updateUserText,
    user,
    users,
  } = props;

  const { openPopup, closePopup, onClosePopup } = usePopupContext();
  const [isSaving, setIsSaving] = useState(false);

  const projectId = projectData.get('id');

  const reopenPopup = useCallback(() => {
    openPopup('ProjectDetailsPopup', { onClose });
  }, [openPopup, onClose]);

  const openChildPopup = useCallback(
    (name, popupProps) => {
      openPopup(name, popupProps);
      onClosePopup(reopenPopup);
    },
    [openPopup, onClosePopup, reopenPopup],
  );

  const handleDiscard = useCallback(() => {
    reset();
    revertProjectChanges();
    Promise.all([loadProject(projectId), loadUsers(projectId), loadIndustries(projectId)]).then(() => {
      if (onClose) {
        onClose();
      }
    });
  }, [reset, revertProjectChanges, loadProject, loadUsers, loadIndustries, projectId, onClose]);

  useEffect(() => {
    onClosePopup(handleDiscard);
  }, [onClosePopup, handleDiscard]);

  const handleSave = useCallback(() => {
    const tags = getIn(common, 'selectedBICs');
    const models = getIn(common, 'selectedBMs');

    if (!tags.size || !models.size) {
      showError(openChildPopup, ['Industry tags or business models are missing'], reopenPopup);

      return;
    }

    setIsSaving(true);
    saveProjectDetails().then(isSaved => {
      setIsSaving(false);
      if (isSaved) {
        closePopup();
      }
    });
  }, [common, openChildPopup, reopenPopup, saveProjectDetails, closePopup]);

  const handleTextChange = useCallback(
    ({ event, data: { type, row } }) => {
      const {
        target: { name, value, checked },
      } = event;

      if (type === types.get('USERS')) {
        updateUserText(row.get('id'), value);
      } else if (type === types.get('PROJECT')) {
        updateProjectInfo(name, event.target.type.toUpperCase() === 'CHECKBOX' ? checked : value);
      } else if (type === types.get('TEMPLATES')) {
        updateTemplateName(row.get('id'), value);
      }
    },
    [updateUserText, updateProjectInfo, updateTemplateName],
  );

  const handleFocus = useCallback(
    (name, value = '') => {
      updateProjectInfo(name, value);
    },
    [updateProjectInfo],
  );

  const handleSuggestionClose = useCallback(
    ({ data }) => {
      const { type, row } = data;

      if (type === 'users') {
        switchUserRowToEdit(row.get('id'));
      }

      if (type === 'project') {
        reset();
      }
    },
    [switchUserRowToEdit, reset],
  );

  const handleSuggestionSelect = useCallback(
    ({ data, suggestion }) => {
      const { type, row, name } = data;

      if (type === types.get('USERS')) {
        selectUserSuggestion(row.get('id'), suggestion.get('id'), suggestion.get('name'));
      } else if (type === types.get('PROJECT')) {
        selectProjectSuggestion(name, suggestion.get('id'), suggestion.get('name'));
      }
    },
    [selectUserSuggestion, selectProjectSuggestion],
  );

  const handleSuggestionInputClick = useCallback(
    ({ event }) => {
      event.stopPropagation();
      reset();
    },
    [reset],
  );

  const handleRowClick = useCallback(
    ({ row, type }) => {
      const id = row.get('id');

      if (type === types.get('USERS')) {
        switchUserRowToEdit(id);
      } else if (type === types.get('TEMPLATES')) {
        switchTemplateRowToEdit(id);
      }
    },
    [switchUserRowToEdit, switchTemplateRowToEdit],
  );

  const handleRowDoubleClick = useCallback(({ row, type }) => {
    if (type === types.get('USERS')) {
      openInternalLink(`/user/${row.get('id')}`);
    }
  }, []);

  const handleRowInsert = useCallback(
    type => {
      if (type === types.get('USERS')) {
        insertUserRow();
      } else if (type === types.get('INDUSTRIES')) {
        const tags = getIn(common, 'selectedBICs');
        const models = getIn(common, 'selectedBMs');

        openChildPopup('TagsManagementPopup', {
          addNew: false,
          clientView: true,
          selectedTags: tags.reduce((accumulator, tag) => {
            accumulator[tag] = true;

            return accumulator;
          }, {}),
          selectedModels: models.reduce((accumulator, model) => {
            accumulator[model] = true;

            return accumulator;
          }, {}),
          callback: (tagSelecteds, modelSelecteds) => updateIndustryTags(tagSelecteds, modelSelecteds),
        });
      } else if (type === types.get('TEMPLATES')) {
        openPopup('TemplatePopup', { projectId, onClose: reopenPopup });
      }
    },
    [insertUserRow, common, openChildPopup, updateIndustryTags, openPopup, projectId, reopenPopup],
  );

  const deleteRowAfterConfirm = useCallback(
    (event, { id, type }) => {
      if (type === types.get('USERS')) {
        deleteUserRow(id);
      } else if (type === types.get('TEMPLATES')) {
        deleteTemplateRow(projectId, id);
      }
    },
    [deleteUserRow, deleteTemplateRow, projectId],
  );

  const handleDeleteRow = useMemo(() => deletionCheck(user, deleteRowAfterConfirm, { openPopup: openChildPopup }), [
    user,
    deleteRowAfterConfirm,
    openChildPopup,
  ]);

  const handleDownload = useCallback(
    (event, { id }) => {
      const template = templates.find(templateItem => templateItem.get('id') === id);

      if (template) {
        downloadFile({ url: template.get('url'), noAuth: true });
      }
    },
    [templates],
  );

  const saveTemplateChange = useCallback(
    row => {
      if (isChanged(row.get('name'))) {
        saveTemplateName(projectId, row.get('id'), getIn(row, 'name'));
      }
    },
    [projectId, saveTemplateName],
  );

  const handleTemplateUpdate = useCallback(
    ({ row }) => {
      saveTemplateChange(row);
    },
    [saveTemplateChange],
  );

  const handleKeyPress = useCallback(
    ({ event, row }) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        saveTemplateChange(row);
        reset();
      }
    },
    [saveTemplateChange, reset],
  );

  const handleReset = useCallback(() => {
    reset();
  }, [reset]);

  const industries = common.get('bic').concat(common.get('bm'));
  const isIndustriesChanged =
    !is(getIn(common, 'selectedBICs'), getOriginalIn(common, 'selectedBICs')) ||
    !is(getIn(common, 'selectedBMs'), getOriginalIn(common, 'selectedBMs'));

  return (
    <Popups.ProjectDetailsPopup
      common={common}
      industries={industries}
      isIndustriesChanged={isIndustriesChanged}
      isSaving={isSaving}
      onCancel={closePopup}
      onDeleteRow={handleDeleteRow}
      onDownload={handleDownload}
      onFocus={handleFocus}
      onKeyPress={handleKeyPress}
      onReset={handleReset}
      onRowClick={handleRowClick}
      onRowDoubleClick={handleRowDoubleClick}
      onRowInsert={handleRowInsert}
      onSave={handleSave}
      onSuggestionClose={handleSuggestionClose}
      onSuggestionInputClick={handleSuggestionInputClick}
      onSuggestionSelect={handleSuggestionSelect}
      onTemplateUpdate={handleTemplateUpdate}
      onTextChange={handleTextChange}
      projectData={projectData}
      templates={templates}
      users={users}
      userSuggestions={common.get('userSuggestions')}
    />
  );
};

ProjectDetailsPopupContainer.propTypes = {
  common: PropTypes.instanceOf(Map).isRequired,
  onClose: PropTypes.func,
  projectData: PropTypes.instanceOf(Map).isRequired,
  templates: PropTypes.instanceOf(List).isRequired,
  user: PropTypes.instanceOf(Map),
  users: PropTypes.instanceOf(List).isRequired,
};

const mapStateToProps = (state, props) => ({
  common: state.project.common,
  onClose: props.popup.getIn(['props', 'onClose']),
  projectData: state.project.projectData,
  templates: state.project.templates,
  user: state.auth.get('user', null),
  users: state.project.users,
});

export { ProjectDetailsPopupContainer };
export default connect(
  mapStateToProps,
  {
    saveProjectDetails,
    loadProject,
    reset,
    revertProjectChanges,
    updateProjectInfo,
    selectProjectSuggestion,
    loadUsers,
    insertUserRow,
    switchUserRowToEdit,
    updateUserText,
    selectUserSuggestion,
    deleteUserRow,
    loadIndustries,
    updateIndustryTags,
    updateTemplateName,
    switchTemplateRowToEdit,
    saveTemplateName,
    deleteTemplateRow,
  },
  mergeProps,
  connectOptions,
)(memo(ProjectDetailsPopupContainer));
